import { Box, Card, IconButton, Stack, styled } from "~/common/mui";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { Table } from "~/common/components/Table/TableElements";

export const CARD_PADDING = 16;

// ── Root card ────────────────────────────────────────────────────────────────

export interface RootProps {
  active?: boolean;
}

export const Root = styled(Card, {
  shouldForwardProp: (prop) => prop !== "active",
})<RootProps>(({ theme, active }) => ({
  background: theme.palette.common.white,
  padding: CARD_PADDING,
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  position: "relative",
  boxShadow: "none",
  border: active ? `1px solid ${theme.palette.secondary.main}` : `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
}));

// ── Header ────────────────────────────────────────────────────────────────────

export const HeaderStyled = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
}));

export const ToggleButton = styled(IconButton)({
  "&&": {
    marginLeft: "auto",
  },
});

export const CheckedIndicatorButton = styled(IconButton)({
  "&&": {
    margin: "-8px",
  },
});

// ── Per-pack label ────────────────────────────────────────────────────────────

export const PerPackStack = styled(Stack)({
  alignItems: "center",
  flexWrap: "wrap",
});

export const Title = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  margin: 0,
}));

export const SaleTagAbsolute = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 2,
});

// ── Collapsible section ───────────────────────────────────────────────────────

export const CollapsibleCardStyled = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexFlow: "column",
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
}));

export const CollapsibleCardContent = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  "& > table": {
    "& th": {
      color: theme.palette.text.label,
    },
    "& th, & td": {
      padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
      maxWidth: "min-content",
    },
  },
}));

export const SectionTitle = styled("p")({
  fontWeight: "bold",
  margin: 0,
  alignItems: "center",
});

export const CollapsedTitleText = styled("p")(({ theme }) => ({
  color: theme.palette.text.label,
}));

// ── Stock table ───────────────────────────────────────────────────────────────

export const StockTableStyled = styled(Table)(({ theme }) => ({
  tableLayout: "fixed",
  "& th:first-of-type": {
    width: 96,
  },
  "& th, & td": {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
}));

export const SalesTableTagSpan = styled("span")(({ theme }) => ({
  display: "inline-flex",
  marginLeft: theme.spacing(4),
}));

// ── Misc section ──────────────────────────────────────────────────────────────

export const MiscSectionStyled = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexDirection: "column",
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
    gap: theme.spacing(2),
  },
  "& > *:nth-of-type(1)": {
    flex: 4,
  },
  "& > *:nth-of-type(2)": {
    flex: 3,
  },
  "& > *:nth-of-type(3)": {
    flex: 3,
  },
}));
